/**
 * @module app.shared
 */

import { autoinject, observable } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { LogService } from 'app/shared/services/log-service';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class LogProcessor {

    @observable
    public currentQueueSize: number = 0;

    private maxQueueSize: number = 10;
    private queueAutoSendTimeout: number = 10000; //In Milliseconds;
    private queueItems: Core.LogEntry[] = [];
    private queueId: number = 0;
    private queueIsRunning: boolean = false;

    constructor(private readonly env: EnvironmentService,
        private readonly service: LogService) {
        this.initialize();
    }

    public flush(): void {
        if(this.queueItems.length > 0) {
            if(this.send(this.queueItems)) {
                this.queueItems = [];
                this.currentQueueSize = 0;
            }
        }
        this.queueId = window.setTimeout(this.processQueue.bind(this), this.queueAutoSendTimeout);
    }

    public startQueue(): void {
        if(!this.queueIsRunning) {
            this.queueId = window.setTimeout(this.processQueue.bind(this), this.queueAutoSendTimeout);
            this.queueIsRunning = true;
        }
    }

    public stopQueue(): void {
        if(this.queueIsRunning) {
            clearTimeout(this.queueId);
            this.queueIsRunning = false;
        }
    }

    public process(item: Core.LogEntry, highPriority: boolean = false): void {
        if(highPriority) {
            if(!this.send([item])) {
                // item not sent, queue it for later
                this.enqueue(item);
            }
        }
        else {
            this.enqueue(item);
        }
    }

    private initialize(): void {
        this.maxQueueSize = this.env.config.logging.maxQueueSize;
        this.queueAutoSendTimeout = this.env.config.logging.queueAutoSendTimeout;
        this.startQueue();
    }

    private enqueue(item: Core.LogEntry): void {
        this.currentQueueSize = this.queueItems.push(item);
    }

    // returns true if successful
    private send(items: Core.LogEntry[]): boolean {
        try {
            if(!this.service.isRemoteLoggingAvailable()) {
                console.debug(`[LOG] Remote Logging not Currently Available, ${items.length} log item(s) not Sent.`);
                return false;
            }
            if(console) {
                console.debug(`[LOG] Send ${items.length} log item(s) to Server...`);
            }

            const application: Core.Application = {
                id: this.env.application.id,
                logEntries: items
            }
            this.service.insertLogs(application);
            return true;
        }
        catch(e) {
            if(console) {
                console.warn('[LOG] Unable to log items to server.', e);
            }
        }
        return false;
    }

    protected currentQueueSizeChanged(): void {
        if(this.currentQueueSize >= this.maxQueueSize) {
            this.flush();
        }
    }

    private processQueue(): void {
        this.flush();
    }

}
